import { currentDivisionId } from 'common/src/stores/useFiltersStore';
import { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Select,
  SelectItem,
  Card,
  CardBody,
  Chip,
} from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { getSettlementsByExporterId } from 'common/src/api/settlements';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import { formatMoneyUSD } from '../../../utils/functions';
import { Icon } from '@iconify/react';
import { groupSettlementsByWeek } from 'common/src/utils/functions';
import ComparativeAnalysisSection from '../../../sections/settlements/comparative-seasons/ComparativeAnalysisSection';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import Header from '../../../components/layouts/Header';
import { EmptyState, FiltersToggle, PageLayout } from '../../../components/ui';
import { SEASONS } from 'common/src/utils/definitions';
import UnitPriceFilter from 'common/src/components/filters/UnitPriceFilter';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';

const ICON_CLASSNAME = 'h-7 w-7 text-default-600';

const SalesIcon = () => <Icon icon='tabler:currency-dollar' className={ICON_CLASSNAME} />;
const VolumeIcon = () => <Icon icon='tabler:scale' className={ICON_CLASSNAME} />;
const PriceIcon = () => <Icon icon='tabler:tag' className={ICON_CLASSNAME} />;

// Filter types
type GroupByKey =
  | 'week'
  | 'variety'
  | 'format'
  | 'receiver'
  | 'caliber'
  | 'color'
  | 'market'
  | 'country'
  | 'label';

type ChartFilters = {
  [key in GroupByKey]: string[];
};

// FILTER_OPTIONS will be defined inside component to access translation hook

interface SeasonTotals {
  totalSales: number;
  totalVolume: number;
  averageFobPrice: number;
  settlementsCount: number;
}

const SettlementsComparativeSeasons = () => {
  const { t } = useTranslation('settlementsComparative');
  const { userProfile } = useUserStore();
  const { selectedSpecies } = useDataStore();

  const FILTER_OPTIONS: Record<GroupByKey, string> = {
    week: t('Semana'),
    variety: t('Variedad'),
    format: t('Formato'),
    receiver: t('Cliente'),
    caliber: t('Calibre'),
    color: t('Color'),
    market: t('Mercado'),
    country: t('País'),
    label: t('Etiqueta'),
  };

  const [seasonA, setSeasonA] = useState<string>('2023-2024');
  const [seasonB, setSeasonB] = useState<string>('2024-2025');
  const [loading, setLoading] = useState<boolean>(false);
  const [settlementsBySeason, setSettlementsBySeason] = useState<{
    seasonA: Settlement[];
    seasonB: Settlement[];
  }>({ seasonA: [], seasonB: [] });
  const [hasAttemptedComparison, setHasAttemptedComparison] =
    useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [priceView, setPriceView] = useState<string>('kg');
  const [dateType, setDateType] = useState<'eta' | 'etd' | 'sale_date'>('eta');

  // Filter states
  const [filters, setFilters] = useState<ChartFilters>({
    week: [],
    variety: [],
    caliber: [],
    color: [],
    market: [],
    country: [],
    receiver: [],
    format: [],
    label: [],
  });

  // Reset seasons when species changes
  useEffect(() => {
    setSeasonA('2023-2024');
    setSeasonB('2024-2025');
    setSettlementsBySeason({ seasonA: [], seasonB: [] });
    setHasAttemptedComparison(false);
  }, [selectedSpecies?.id]);

  const canCompare = seasonA && seasonB && seasonA !== seasonB;

  // Get filter options from all settlements
  const filterOptions = useMemo(() => {
    const allSettlements = [
      ...settlementsBySeason.seasonA,
      ...settlementsBySeason.seasonB,
    ];

    const allSettlementDetails = allSettlements.flatMap((s) =>
      s.settlement_details.map((d) => ({
        ...d,
        settlement_id: s.id,
        receiver: s.receiver,
        market: s.market?.name || 'N/A',
        country: s.market?.country || 'N/A',
      }))
    );

    // Define getDate function based on dateType
    const getDate = (settlement: Settlement): string => {
      if (dateType === 'eta') {
        return settlement.ship_trip?.eta || settlement.date_sold;
      } else if (dateType === 'etd') {
        return settlement.ship_trip?.etd || settlement.date_sold;
      } else {
        return settlement.date_sold;
      }
    };

    const groupedByWeek = groupSettlementsByWeek(allSettlements, getDate);

    const getGenericWeek = (
      fullWeek: string | undefined
    ): string | undefined => {
      if (!fullWeek) return undefined;
      const parts = fullWeek.split('-');
      return `W${parts[parts.length - 1].replace('W', '')}`;
    };

    const sortWeeksCustom = (a: string, b: string): number => {
      const numA = parseInt(a.slice(1), 10);
      const numB = parseInt(b.slice(1), 10);

      const groupA = numA >= 24 ? 1 : 2;
      const groupB = numB >= 24 ? 1 : 2;

      if (groupA !== groupB) {
        return groupA - groupB;
      }

      return numA - numB;
    };

    return {
      week: [
        ...new Set(
          Object.keys(groupedByWeek)
            .map(getGenericWeek)
            .filter((w): w is string => !!w)
        ),
      ].sort(sortWeeksCustom),
      variety: [...new Set(allSettlementDetails.map((d) => d.variety))],
      caliber: [...new Set(allSettlementDetails.map((d) => d.caliber))],
      color: [...new Set(allSettlementDetails.map((d) => d.color))],
      market: [...new Set(allSettlementDetails.map((d) => d.market))],
      country: [...new Set(allSettlementDetails.map((d) => d.country))],
      receiver: [...new Set(allSettlements.map((s) => s.receiver))],
      format: [
        ...new Set(
          allSettlementDetails.map((d) => `${d.format}-${d.unit_weight}KG`)
        ),
      ],
      label: [...new Set(allSettlementDetails.map((d) => d.label))],
    };
  }, [settlementsBySeason, dateType]);

  // Filter settlements based on current filters
  const getFilteredSettlements = (settlements: Settlement[]): Settlement[] => {
    const activeFilterEntries = Object.entries(filters).filter(
      ([, values]) => values && values.length > 0
    );

    if (activeFilterEntries.length === 0) {
      return settlements;
    }

    // Solo necesitamos agrupar por semana si hay filtro de semana activo
    let settlementIdToWeekMap = new Map<string, string>();

    if (filters.week?.length > 0) {
      // Define getDate function based on dateType
      const getDate = (settlement: Settlement): string => {
        if (dateType === 'eta') {
          return settlement.ship_trip?.eta || settlement.date_sold;
        } else if (dateType === 'etd') {
          return settlement.ship_trip?.etd || settlement.date_sold;
        } else {
          return settlement.date_sold;
        }
      };

      const groupedByWeek = groupSettlementsByWeek(settlements, getDate);

      for (const week in groupedByWeek) {
        for (const settlement of groupedByWeek[week]) {
          settlementIdToWeekMap.set(String(settlement.id), week);
        }
      }
    }

    const getGenericWeek = (
      fullWeek: string | undefined
    ): string | undefined => {
      if (!fullWeek) return undefined;
      const parts = fullWeek.split('-');
      return `W${parts[parts.length - 1].replace('W', '')}`;
    };

    return settlements
      .map((s) => {
        const settlementWeek = getGenericWeek(
          settlementIdToWeekMap.get(String(s.id))
        );

        // Check settlement-level filters
        if (
          filters.week?.length &&
          (!settlementWeek || !filters.week.includes(settlementWeek))
        ) {
          return null;
        }
        if (
          filters.receiver?.length &&
          !filters.receiver.includes(s.receiver)
        ) {
          return null;
        }
        if (
          filters.market?.length &&
          !filters.market.includes(s.market?.name || 'N/A')
        ) {
          return null;
        }
        if (
          filters.country?.length &&
          !filters.country.includes(s.market?.country || 'N/A')
        ) {
          return null;
        }

        // Filter settlement details
        const filteredDetails = s.settlement_details.filter((d) => {
          if (filters.variety?.length && !filters.variety.includes(d.variety))
            return false;
          if (filters.caliber?.length && !filters.caliber.includes(d.caliber))
            return false;
          if (filters.color?.length && !filters.color.includes(d.color))
            return false;
          const detailFormat = `${d.format}-${d.unit_weight}KG`;
          if (filters.format?.length && !filters.format.includes(detailFormat))
            return false;
          if (filters.label?.length && !filters.label.includes(d.label))
            return false;
          return true;
        });

        if (filteredDetails.length === 0) {
          return null;
        }

        return { ...s, settlement_details: filteredDetails };
      })
      .filter((s): s is Settlement => s !== null);
  };

  // Apply filters to both seasons
  const filteredSettlements = useMemo(() => {
    return {
      seasonA: getFilteredSettlements(settlementsBySeason.seasonA),
      seasonB: getFilteredSettlements(settlementsBySeason.seasonB),
    };
  }, [settlementsBySeason, filters, dateType]);

  // Check if there's data to compare
  const hasDataToCompare = useMemo(() => {
    const hasSeasonAData = filteredSettlements.seasonA.length > 0;
    const hasSeasonBData = filteredSettlements.seasonB.length > 0;
    return hasSeasonAData && hasSeasonBData;
  }, [filteredSettlements]);

  const noDataMessage = useMemo(() => {
    const hasSeasonAData = filteredSettlements.seasonA.length > 0;
    const hasSeasonBData = filteredSettlements.seasonB.length > 0;

    if (!hasSeasonAData && !hasSeasonBData) {
      return t('No hay datos disponibles para las temporadas {seasonA} y {seasonB} con la especie seleccionada.', { seasonA, seasonB });
    } else if (!hasSeasonAData) {
      return t('No hay datos disponibles para la temporada {seasonA} con la especie seleccionada.', { seasonA });
    } else if (!hasSeasonBData) {
      return t('No hay datos disponibles para la temporada {seasonB} con la especie seleccionada.', { seasonB });
    }
    return '';
  }, [filteredSettlements, seasonA, seasonB, t]);

  const calculateSeasonTotals = (settlements: Settlement[]): SeasonTotals => {
    let totalSales = 0;
    let totalVolume = 0;
    let totalFobForAverage = 0;
    let totalKilosForAverage = 0;

    settlements.forEach((settlement) => {
      settlement.settlement_details.forEach((detail) => {
        const fob = detail.usd_total_fob || 0;
        const kilos = detail.kilos || 0;

        totalSales += fob;
        totalVolume += kilos;

        totalFobForAverage += fob;
        totalKilosForAverage += kilos;
      });
    });

    let averageFobPrice =
      totalKilosForAverage > 0 ? totalFobForAverage / totalKilosForAverage : 0;

    // Si estamos viendo por caja equivalente, multiplicar por el tamaño de la caja
    if (priceView.startsWith('box-')) {
      const boxSize = parseInt(priceView.split('-')[1]);
      averageFobPrice *= boxSize;
    }

    return {
      totalSales,
      totalVolume,
      averageFobPrice,
      settlementsCount: settlements.length,
    };
  };

  const seasonTotals = useMemo(() => {
    const seasonATotals = calculateSeasonTotals(filteredSettlements.seasonA);
    const seasonBTotals = calculateSeasonTotals(filteredSettlements.seasonB);

    return {
      seasonA: seasonATotals,
      seasonB: seasonBTotals,
    };
  }, [filteredSettlements, priceView]);

  const calculatePercentageDifference = (
    valueA: number,
    valueB: number
  ): number => {
    if (valueA === 0) return valueB > 0 ? 100 : 0;
    return ((valueB - valueA) / valueA) * 100;
  };

  const handleFilterChange = (filterName: GroupByKey, value: string[]) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const fetchSettlementsForSeason = async (
    season: string
  ): Promise<Settlement[]> => {
    const settlements = await getSettlementsByExporterId(
      +userProfile?.exporter_id!,
      'approved',
      season,
      selectedSpecies?.id,
      'all',
      ['*'],
      currentDivisionId()
    );

    if (!settlements) return [];

    return settlements.filter(
      (s) =>
        s.total_freight_usd !== null &&
        s.total_freight_usd > 0 &&
        s.exchange_rate !== null
    );
  };

  const handleCompare = async () => {
    if (!canCompare) return;

    try {
      setLoading(true);

      const [seasonASettlements, seasonBSettlements] = await Promise.all([
        fetchSettlementsForSeason(seasonA),
        fetchSettlementsForSeason(seasonB),
      ]);

      setSettlementsBySeason({
        seasonA: seasonASettlements,
        seasonB: seasonBSettlements,
      });
      console.log(seasonASettlements, seasonBSettlements);
      setHasAttemptedComparison(true);
    } catch (error) {
      console.error('Error fetching settlements:', error);
      setHasAttemptedComparison(true); // También marcar como intentado si hay error
    } finally {
      setLoading(false);
    }
  };

  const formatVolume = (volume: number): string => {
    return (
      new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(volume) + ' kg'
    );
  };

  const hasResults =
    settlementsBySeason.seasonA.length > 0 ||
    settlementsBySeason.seasonB.length > 0 ||
    hasAttemptedComparison;

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      {/* Filter Sidebar */}
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            <p className='text-sm text-default-500 mb-1'>{t('Filtros')}</p>

            {/* Primeros filtros */}
            {['week', 'variety', 'format', 'receiver'].map((key) => (
              <Select
                key={key}
                label={FILTER_OPTIONS[key as GroupByKey]}
                placeholder={t('Todos los {filter}s', { filter: FILTER_OPTIONS[key as GroupByKey] })}
                size='sm'
                selectionMode='multiple'
                selectedKeys={filters[key as GroupByKey]}
                onSelectionChange={(keys) =>
                  handleFilterChange(
                    key as GroupByKey,
                    Array.from(keys) as string[]
                  )
                }
                isDisabled={!hasResults}
              >
                {(filterOptions[key as GroupByKey] || []).map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </Select>
            ))}

            {/* Nuevos filtros en la mitad */}
            <Select
              label={t('Criterio de Fecha')}
              selectedKeys={[dateType]}
              defaultSelectedKeys={['eta']}
              onSelectionChange={(keys) =>
                setDateType(Array.from(keys)[0] as 'eta' | 'etd' | 'sale_date')
              }
              className='w-full'
              size='sm'
              isDisabled={!hasResults}
            >
              <SelectItem key='eta' value='eta'>
                {t('ETA (Fecha de Arribo)')}
              </SelectItem>
              <SelectItem key='etd' value='etd'>
                {t('ETD (Fecha de Zarpe)')}
              </SelectItem>
              <SelectItem key='sale_date' value='sale_date'>
                {t('Fecha de Venta')}
              </SelectItem>
            </Select>

            <UnitPriceFilter
              selectedSpecies={selectedSpecies}
              value={priceView}
              onChange={setPriceView}
              label={t('Unidad de precio')}
              size='sm'
            />

            {/* Resto de filtros */}
            {['caliber', 'color', 'market', 'country', 'label'].map((key) => (
              <Select
                key={key}
                label={FILTER_OPTIONS[key as GroupByKey]}
                placeholder={t('Todos los {filter}s', { filter: FILTER_OPTIONS[key as GroupByKey] })}
                size='sm'
                selectionMode='multiple'
                selectedKeys={filters[key as GroupByKey]}
                onSelectionChange={(keys) =>
                  handleFilterChange(
                    key as GroupByKey,
                    Array.from(keys) as string[]
                  )
                }
                isDisabled={!hasResults}
              >
                {(filterOptions[key as GroupByKey] || []).map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </Select>
            ))}
          </div>
        </div>
      </ModalSlideFilter>

      <PageLayout>
        <Header
          title={t('Comparativo Temporadas')}
          description={t('Selecciona dos temporadas y compara métricas clave como ventas, volumen y precios para analizar el rendimiento de tus clientes.')}
        />
        {hasResults && (
          <div className='mb-4'>
            <FiltersToggle
              isOpen={isFilterOpen}
              onToggle={() => setIsFilterOpen(!isFilterOpen)}
              label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
            />
          </div>
        )}

        <Card>
          <CardBody className='p-6'>
            <div className='space-y-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <Select
                    label={t('Primera temporada (A)')}
                    placeholder={t('Elige una temporada')}
                    selectedKeys={seasonA ? [seasonA] : []}
                    onSelectionChange={(keys) =>
                      setSeasonA(Array.from(keys as Set<string>)[0] || '')
                    }
                    className='w-full'
                  >
                    {Object.values(SEASONS).map((season) => (
                      <SelectItem
                        key={season.name}
                        value={season.name}
                        isDisabled={season.name === seasonB}
                      >
                        {season.name}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                <div>
                  <Select
                    label={t('Segunda temporada (B)')}
                    placeholder={t('Elige otra temporada')}
                    selectedKeys={seasonB ? [seasonB] : []}
                    onSelectionChange={(keys) =>
                      setSeasonB(Array.from(keys as Set<string>)[0] || '')
                    }
                    className='w-full'
                  >
                    {Object.values(SEASONS).map((season) => (
                      <SelectItem
                        key={season.name}
                        value={season.name}
                        isDisabled={season.name === seasonA}
                      >
                        {season.name}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>

              <div className='flex justify-end pt-2'>
                <Button
                  color='primary'
                  // size='lg'
                  onClick={handleCompare}
                  isDisabled={!canCompare}
                  isLoading={loading}
                >
                  {loading ? t('Analizando...') : t('Comparar Temporadas')}
                </Button>
              </div>

              {!canCompare && (seasonA || seasonB) && (
                <div className='text-left'>
                  <p className='text-sm text-default-400'>
                    {seasonA === seasonB
                      ? t('⚠️ Selecciona dos temporadas diferentes para poder comparar.')
                      : t('👆 Selecciona ambas temporadas para continuar.')}
                  </p>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {hasResults && (
          <>
            {!hasDataToCompare ? (
              <div className='mt-6'>
                <EmptyState
                  icon='tabler:chart-bar'
                  title={t('No hay datos para comparar')}
                  description={noDataMessage}
                  action={
                    <div className='text-sm text-default-400'>
                      <p>{t('Prueba seleccionando:')}</p>
                      <ul className='list-disc list-inside mt-2 space-y-1'>
                        <li>{t('Diferentes temporadas')}</li>
                        <li>{t('Otra especie en el filtro superior')}</li>
                        <li>
                          {t('Verificar que existan liquidaciones para estas temporadas')}
                        </li>
                      </ul>
                    </div>
                  }
                />
              </div>
            ) : (
              <>
                <Card className='p-4 mt-6'>
                  <CardBody>
                    <h2 className='text-lg font-semibold text-foreground mb-4'>
                      {t('Resultados de la Comparación')}
                    </h2>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
                      {/* Season A Column */}
                      <div className='space-y-6'>
                        <div className='p-4 bg-blue-50 dark:bg-blue-950 rounded-xl border border-blue-200'>
                          <h3 className='text-lg font-semibold text-blue-800'>
                            {seasonA}
                          </h3>
                          <p className='text-sm text-blue-700'>
                            {seasonTotals.seasonA.settlementsCount}{' '}
                            {t('liquidaciones analizadas')}
                          </p>
                        </div>
                        <MetricDisplay
                          icon={<SalesIcon />}
                          title={t('Ventas Totales')}
                          value={formatMoneyUSD(
                            seasonTotals.seasonA.totalSales,
                            {
                              decimals: 2,
                            }
                          )}
                        />
                        <MetricDisplay
                          icon={<VolumeIcon />}
                          title={t('Volumen Total')}
                          value={formatVolume(seasonTotals.seasonA.totalVolume)}
                        />
                        <MetricDisplay
                          icon={<PriceIcon />}
                          title={t('Precio FOB Promedio')}
                          value={`${formatMoneyUSD(
                            seasonTotals.seasonA.averageFobPrice,
                            {
                              decimals: 2,
                            }
                          )}/${priceView === 'kg' ? 'kg' : t('caja eq.')}`}
                        />
                      </div>

                      {/* Season B Column */}
                      <div className='space-y-6'>
                        <div className='p-4 bg-green-50 dark:bg-green-950 rounded-xl border border-green-200'>
                          <h3 className='text-lg font-semibold text-green-800'>
                            {seasonB}
                          </h3>
                          <p className='text-sm text-green-700'>
                            {seasonTotals.seasonB.settlementsCount}{' '}
                            {t('liquidaciones analizadas')}
                          </p>
                        </div>
                        <MetricDisplay
                          icon={<SalesIcon />}
                          title={t('Ventas Totales')}
                          value={formatMoneyUSD(
                            seasonTotals.seasonB.totalSales,
                            {
                              decimals: 2,
                            }
                          )}
                          percentChange={calculatePercentageDifference(
                            seasonTotals.seasonA.totalSales,
                            seasonTotals.seasonB.totalSales
                          )}
                        />
                        <MetricDisplay
                          icon={<VolumeIcon />}
                          title={t('Volumen Total')}
                          value={formatVolume(seasonTotals.seasonB.totalVolume)}
                          percentChange={calculatePercentageDifference(
                            seasonTotals.seasonA.totalVolume,
                            seasonTotals.seasonB.totalVolume
                          )}
                        />
                        <MetricDisplay
                          icon={<PriceIcon />}
                          title={t('Precio FOB Promedio')}
                          value={`${formatMoneyUSD(
                            seasonTotals.seasonB.averageFobPrice,
                            {
                              decimals: 2,
                            }
                          )}/${priceView === 'kg' ? 'kg' : t('caja eq.')}`}
                          percentChange={calculatePercentageDifference(
                            seasonTotals.seasonA.averageFobPrice,
                            seasonTotals.seasonB.averageFobPrice
                          )}
                        />
                      </div>
                    </div>
                    <p className='text-xs text-default-400 mt-6'>
                      {t('* Los porcentajes de variación se calculan tomando la Temporada A como referencia.')}
                    </p>
                  </CardBody>
                </Card>

                {/* Detailed Analysis Section */}
                <div className='mt-6'>
                  <ComparativeAnalysisSection
                    settlementsA={filteredSettlements.seasonA}
                    settlementsB={filteredSettlements.seasonB}
                    seasonA={seasonA}
                    seasonB={seasonB}
                    priceView={priceView}
                    dateType={dateType}
                  />
                </div>
              </>
            )}
          </>
        )}
      </PageLayout>
    </div>
  );
};

const MetricDisplay = ({
  icon,
  title,
  value,
  percentChange,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  percentChange?: number;
}) => {
  const isPositive = percentChange !== undefined && percentChange >= 0;

  return (
    <Card className='shadow-sm'>
      <CardBody>
        <div className='flex justify-between items-center'>
          <div className='flex items-center'>
            <div className='flex-shrink-0 mr-4'>{icon}</div>
            <div>
              <p className='text-sm font-medium text-default-500'>{title}</p>
              <p className='text-xl font-bold text-foreground'>{value}</p>
            </div>
          </div>
          {percentChange !== undefined && (
            <Chip
              color={isPositive ? 'success' : 'danger'}
              variant='flat'
              size='sm'
            >
              {isPositive ? '▲' : '▼'} {percentChange.toFixed(1)}%
            </Chip>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default SettlementsComparativeSeasons;
